import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ImageUpload from './ImageUpload';

function EditProfile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    bio: '',
    experience: '',
    phone: '',
    website: '',
    specialties: [],
    profileImage: '',
    profileImagePath: ''
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [uploadError, setUploadError] = useState('');

  const steps = [
    { id: 1, title: 'BASIC INFO', description: 'Name and photo' },
    { id: 2, title: 'PROFESSIONAL', description: 'Title and experience' },
    { id: 3, title: 'ABOUT YOU', description: 'Bio and specialties' },
    { id: 4, title: 'CONTACT', description: 'How to reach you' }
  ];

  useEffect(() => {
    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser]);

  const fetchProfile = async () => {
    try {
      const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        setFormData({
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          title: profileData.title || '',
          bio: profileData.bio || '',
          experience: profileData.experience || '',
          phone: profileData.phone || '',
          website: profileData.website || '',
          specialties: profileData.specialties || [],
          profileImage: profileData.profileImage || '',
          profileImagePath: profileData.profileImagePath || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSpecialty = () => {
    if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty.trim()]
      }));
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (index) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter((_, i) => i !== index)
    }));
  };

  const handleImageUploadComplete = (uploadResult) => {
    setFormData(prev => ({
      ...prev,
      profileImage: uploadResult.url,
      profileImagePath: uploadResult.path
    }));
    setUploadError('');
  };

  const handleImageUploadError = (error) => {
    setUploadError(error);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.firstName.trim() && formData.lastName.trim();
      case 2:
        return true; // Title and experience are optional
      case 3:
        return formData.bio.trim();
      case 4:
        return true; // Contact info is optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepNumber) => {
    // Allow going back to any step, but only forward if previous steps are valid
    if (stepNumber <= currentStep || (stepNumber > currentStep && isAllPreviousStepsValid(stepNumber))) {
      setCurrentStep(stepNumber);
    }
  };

  const isAllPreviousStepsValid = (targetStep) => {
    for (let i = 1; i < targetStep; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required steps
    if (!isStepValid(1) || !isStepValid(3)) {
      alert('Please complete all required fields before saving.');
      return;
    }

    setSaving(true);

    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...formData,
        email: currentUser.email,
        userType: 'instructor', // Ensure user type is set
        updatedAt: new Date().toISOString()
      }, { merge: true });

      navigate('/profile');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    return formData.firstName.trim() && formData.lastName.trim() && formData.bio.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-klorah-dark flex items-center justify-center">
        <div className="text-white">Loading profile...</div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2 tracking-wider">LET'S START WITH THE BASICS</h2>
              <p className="text-klorah-text">Your name and photo help students connect with you</p>
            </div>

            <div className="glass-panel p-8 space-y-6 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                    FIRST NAME *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                    LAST NAME *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  PROFILE PHOTO
                </label>
                <ImageUpload
                  onUploadComplete={handleImageUploadComplete}
                  onUploadError={handleImageUploadError}
                  currentImage={formData.profileImage}
                  uploadPath={`profiles/${currentUser.uid}`}
                />
                {uploadError && (
                  <p className="text-red-400 text-sm mt-2">{uploadError}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2 tracking-wider">PROFESSIONAL INFORMATION</h2>
              <p className="text-klorah-text">Tell students about your professional background</p>
            </div>

            <div className="glass-panel p-8 space-y-6 rounded-xl">
              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  PROFESSIONAL TITLE
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                  placeholder="e.g., Professional Photographer, Photography Instructor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  YEARS OF EXPERIENCE
                </label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary"
                >
                  <option value="">Select experience level</option>
                  <option value="1-2 years">1-2 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="6-10 years">6-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2 tracking-wider">ABOUT YOU</h2>
              <p className="text-klorah-text">Share your story and expertise areas</p>
            </div>

            <div className="glass-panel p-8 space-y-6 rounded-xl">
              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  BIO *
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light resize-none"
                  placeholder="Tell students about your background, teaching style, and what makes your workshops special..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  SPECIALTIES
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSpecialty}
                      onChange={(e) => setNewSpecialty(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                      className="flex-1 px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                      placeholder="e.g., Portrait Photography"
                    />
                    <button
                      type="button"
                      onClick={addSpecialty}
                      className="px-6 py-3 bg-klorah-primary/20 text-klorah-primary border border-klorah-primary/30 rounded-xl hover:bg-klorah-primary/30 transition-all font-medium"
                    >
                      ADD
                    </button>
                  </div>
                  
                  {formData.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 bg-klorah-primary/10 border border-klorah-primary/30 text-klorah-primary text-sm rounded-full"
                        >
                          {specialty}
                          <button
                            type="button"
                            onClick={() => removeSpecialty(index)}
                            className="ml-2 text-klorah-primary hover:text-white transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2 tracking-wider">CONTACT INFORMATION</h2>
              <p className="text-klorah-text">How students can reach you (optional)</p>
            </div>

            <div className="glass-panel p-8 space-y-6 rounded-xl">
              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  PHONE NUMBER
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2 tracking-wider">
                  WEBSITE
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-klorah-primary focus:border-klorah-primary placeholder-klorah-text-light"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-klorah-dark">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => handleStepClick(step.id)}
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 text-sm font-medium transition-all ${
                    currentStep === step.id
                      ? 'bg-klorah-primary border-klorah-primary text-black'
                      : step.id < currentStep
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-klorah-card border-klorah-border text-klorah-text hover:border-klorah-primary'
                  }`}
                >
                  {step.id < currentStep ? '✓' : step.id}
                </button>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 rounded transition-all ${
                    step.id < currentStep ? 'bg-emerald-500' : 'bg-klorah-border'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <h1 className="text-2xl font-bold text-white tracking-wider">
              {steps[currentStep - 1].title}
            </h1>
            <p className="text-klorah-text">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        {/* Step Content */}
        <form onSubmit={handleSubmit}>
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed tracking-wider"
            >
              PREVIOUS
            </button>

            <div className="flex space-x-4">
              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isStepValid(currentStep)}
                  className="klorah-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  NEXT
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!isFormValid() || saving}
                  className="px-6 py-3 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-medium rounded-xl hover:bg-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed tracking-wider"
                >
                  {saving ? 'SAVING...' : 'SAVE PROFILE'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfile; 